/**
 * hero.styles.js
 * Design tokens and shared motion variants for EnterpriseHeroV2.
 * Keeping these centralized avoids drift between sibling components
 * (HeroContent, HeroButtons, HeroStats, HeroVisualization all import from here).
 */

// ── Brand tokens (kept exactly as specified — do not change) ──────────────
export const COLORS = {
  bg:        "#000000",
  primary:   "#1E90FF",
  primaryDark: "#1565C0",
  primaryLight: "#42A5F5",
  white:     "rgba(255,255,255,0.97)",
  textMuted: "rgba(255,255,255,0.56)",
  textFaint: "rgba(255,255,255,0.38)",
  border:    "rgba(255,255,255,0.10)",
  borderHover: "rgba(30,144,255,0.4)",
  glassBg:   "rgba(255,255,255,0.035)",
};

export const FONTS = {
  display: "'Bricolage Grotesque', sans-serif",
  body:    "'DM Sans', sans-serif",
};

// ── Header offset ───────────────────────────────────────────────────────
// Header1 renders a 64px in-flow spacer above all page content.
// Every height/minHeight calc in this hero subtracts this value so the
// hero never overlaps the fixed header AND never creates a structural
// scrollbar (document height = spacer + hero height, must equal exactly
// one viewport when hero uses minHeight: calc(100dvh - HEADER_HEIGHT)).
export const HEADER_HEIGHT = {
  xs: 64,
  md: 64,
  lg: 64,
};

// ── Motion variants ─────────────────────────────────────────────────────
// Calm, restrained entrance choreography. No infinite bounce/pulse loops
// on content elements — only the background gets slow ambient motion.
const EASE = [0.22, 1, 0.36, 1];

export const fadeUpVariant = (delay = 0, distance = 14) => ({
  initial: { opacity: 0, y: distance },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10% 0px -10% 0px" },
  transition: { duration: 0.6, delay, ease: EASE },
});

export const fadeVariant = (delay = 0) => ({
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 0.6, delay, ease: EASE },
});

export const scaleFadeVariant = (delay = 0) => ({
  initial: { opacity: 0, scale: 0.96 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: 0.5, delay, ease: EASE },
});

export const slideFromRightVariant = (delay = 0) => ({
  initial: { opacity: 0, x: 28 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: "-10% 0px -10% 0px" },
  transition: { duration: 0.7, delay, ease: EASE },
});

// Stagger container for stats / list-like groups
export const staggerContainer = (staggerDelay = 0.08, initialDelay = 0) => ({
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, margin: "-10% 0px -10% 0px" },
  transition: { staggerChildren: staggerDelay, delayChildren: initialDelay },
});

export const staggerItem = {
  hidden:  { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

// ── Reduced motion ──────────────────────────────────────────────────────
// Components check this and fall back to opacity-only or instant states.
export const REDUCED_MOTION_MEDIA = "(prefers-reduced-motion: reduce)";